import React from 'react';
import { Alert, Button, Card, Col, Form, Input, Row, Select, Space, Tag, Typography } from 'antd';
import { DownOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import type { ModuleDefinitionItem } from './types';
import ModuleConfigEditor, { deepMergeConfig } from './ModuleConfigEditor';

const { Text } = Typography;

interface ModuleFormListProps {
  form: FormInstance;
  moduleDefinitions: ModuleDefinitionItem[];
  name?: string;
  title?: string;
  emptyDescription?: string;
  visualOnly?: boolean;
  showMetadata?: boolean;
}

const findDefinition = (moduleDefinitions: ModuleDefinitionItem[], id: unknown): ModuleDefinitionItem | undefined => {
  if (id === undefined || id === null || id === '') return undefined;
  const numericId = Number(id);
  return moduleDefinitions.find((def) => Number(def.id) === numericId);
};

const coerceDefinition = (value: any): ModuleDefinitionItem | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  if (value.id === undefined || value.id === null) return undefined;
  return value as ModuleDefinitionItem;
};

const mergeDefinitionDefaults = (definition: ModuleDefinitionItem | undefined, config: any): Record<string, any> => {
  if (!definition?.defaultConfig) {
    return config && typeof config === 'object' && !Array.isArray(config) ? config : {};
  }
  return deepMergeConfig(definition.defaultConfig, config && typeof config === 'object' && !Array.isArray(config) ? config : {});
};

const statusColor = (status?: string) => {
  if (status === 'installed') return 'green';
  if (status === 'maintenance') return 'gold';
  if (status === 'disabled') return 'default';
  if (status === 'removed') return 'red';
  return 'default';
};

const ModuleFormList: React.FC<ModuleFormListProps> = ({
  form,
  moduleDefinitions,
  name = 'moduleTemplates',
  title = 'module',
  emptyDescription = 'Module templates describe what devices of this type normally ship with.',
  visualOnly = false,
  showMetadata = true,
}) => {
  const [expandedKeys, setExpandedKeys] = React.useState<Set<number | string>>(() => new Set());
  const toggleExpanded = React.useCallback((key: number | string) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              add({ moduleDefinitionId: undefined, status: 'installed', isDefault: fields.length === 0, config: {}, configText: '' });
              window.setTimeout(() => {
                const cards = document.querySelectorAll(`[data-module-form-list="${name}"]`);
                const lastCard = cards[cards.length - 1];
                lastCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 80);
            }}
          >
            Add {title}
          </Button>
        </Space>

        {fields.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message={`No ${title}s yet`}
            description={emptyDescription}
          />
        ) : (
          fields.map((field, index) => (
            <Form.Item noStyle shouldUpdate key={field.key}>
              {() => {
                const modules = form.getFieldValue(name) || [];
                const row = modules?.[field.name] || {};
                const embeddedDefinition = coerceDefinition(row.moduleDefinition);
                const selectedDefinition = findDefinition(moduleDefinitions, row.moduleDefinitionId) || embeddedDefinition;
                const expanded = expandedKeys.has(field.key);
                const displayName = row.name || selectedDefinition?.name || `${title.charAt(0).toUpperCase()}${title.slice(1)} ${index + 1}`;
                const instanceKey = row.instanceKey || 'No instance key';
                const definitionOptions = selectedDefinition && !findDefinition(moduleDefinitions, selectedDefinition.id)
                  ? [
                    { value: selectedDefinition.id, label: `${selectedDefinition.name} (${selectedDefinition.key})` },
                    ...moduleDefinitions.map((def) => ({ value: def.id, label: `${def.name} (${def.key})` })),
                  ]
                  : moduleDefinitions.map((def) => ({ value: def.id, label: `${def.name} (${def.key})` }));

                return (
                  <Card
                    size="small"
                    data-module-form-list={name}
                    bodyStyle={{ padding: expanded ? 12 : '8px 12px' }}
                    title={
                      <Space size="small" wrap>
                        <Text strong>{displayName}</Text>
                        {selectedDefinition?.key && <Tag color="blue">{selectedDefinition.key}</Tag>}
                        <Tag color={statusColor(row.status)}>{row.status || 'installed'}</Tag>
                        {row.isDefault && <Tag>default</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>{instanceKey}</Text>
                      </Space>
                    }
                    extra={
                      <Space size="small">
                        <Button
                          size="small"
                          icon={expanded ? <DownOutlined /> : <RightOutlined />}
                          onClick={() => toggleExpanded(field.key)}
                        >
                          {expanded ? 'Collapse' : 'Expand'}
                        </Button>
                        <Button size="small" type="link" danger onClick={() => remove(field.name)}>Remove</Button>
                      </Space>
                    }
                  >
                    {!expanded ? (
                      <Space size={4} wrap>
                        {selectedDefinition?.moduleType && <Tag>{selectedDefinition.moduleType}</Tag>}
                        {selectedDefinition?.dataType && <Tag>{selectedDefinition.dataType}</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {selectedDefinition?.configSchema ? 'Configuration available' : 'No schema available'}
                        </Text>
                      </Space>
                    ) : (
                      <div>
                        <Row gutter={[8, 0]}>
                          <Col xs={24} md={visualOnly ? 8 : 10}>
                            <Form.Item
                              label="Module definition"
                              name={[field.name, 'moduleDefinitionId']}
                              rules={[{ required: true, message: 'Module definition is required' }]}
                            >
                              <Select
                                size="small"
                                showSearch
                                placeholder="Select module definition"
                                optionFilterProp="label"
                                options={definitionOptions}
                                onChange={(value: number | string | undefined) => {
                                  if (!value) return;
                                  const def = findDefinition(moduleDefinitions, value) || (Number(embeddedDefinition?.id) === Number(value) ? embeddedDefinition : undefined);
                                  if (!def) return;
                                  const mods = form.getFieldValue(name) || [];
                                  mods[field.name] = {
                                    ...mods[field.name],
                                    moduleDefinition: def,
                                    name: mods[field.name]?.name || def.name,
                                    config: mergeDefinitionDefaults(def, mods[field.name]?.config),
                                    configText: mods[field.name]?.configText || (def.defaultConfig ? JSON.stringify(def.defaultConfig, null, 2) : ''),
                                  };
                                  form.setFieldsValue({ [name]: mods });
                                }}
                              />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={visualOnly ? 5 : 6}>
                            <Form.Item label="Display name" name={[field.name, 'name']}>
                              <Input size="small" placeholder="Front RGB camera" />
                            </Form.Item>
                          </Col>

                          <Col xs={24} md={5}>
                            <Form.Item label="Instance key" name={[field.name, 'instanceKey']}>
                              <Input size="small" placeholder="cam_front" />
                            </Form.Item>
                          </Col>

                          {visualOnly && (
                            <Col xs={12} md={3}>
                              <Form.Item label="Status" name={[field.name, 'status']}>
                                <Select
                                  size="small"
                                  options={[
                                    { value: 'installed', label: 'Installed' },
                                    { value: 'disabled', label: 'Disabled' },
                                    { value: 'maintenance', label: 'Maintenance' },
                                    { value: 'removed', label: 'Removed' },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                          )}

                          <Col xs={12} md={3}>
                            <Form.Item label="Default?" name={[field.name, 'isDefault']}>
                              <Select
                                size="small"
                                options={[
                                  { value: true, label: 'Yes' },
                                  { value: false, label: 'No' },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        {selectedDefinition?.configSchema ? (
                          <div>
                            {visualOnly && (
                              <Space size={4} style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                                <Tag color="blue">{selectedDefinition.key}</Tag>
                                {selectedDefinition.moduleType && <Tag>{selectedDefinition.moduleType}</Tag>}
                                {selectedDefinition.dataType && <Tag>{selectedDefinition.dataType}</Tag>}
                              </Space>
                            )}
                            <Form.Item
                              label={visualOnly ? undefined : 'Configuration'}
                              name={[field.name, 'config']}
                              extra={visualOnly ? undefined : 'Physical or template configuration validated against the module definition schema.'}
                            >
                              <ModuleConfigEditor
                                schema={selectedDefinition.configSchema}
                                defaultValue={selectedDefinition.defaultConfig || {}}
                              />
                            </Form.Item>
                          </div>
                        ) : visualOnly ? (
                          <Alert
                            type="info"
                            showIcon
                            message="No visual configuration fields"
                            description="This module definition is not available with a configuration schema in the current catalogue response."
                          />
                        ) : (
                          <Form.Item
                            label="Config JSON"
                            name={[field.name, 'configText']}
                            extra="Used when the selected module definition does not provide a schema."
                          >
                            <Input.TextArea rows={4} placeholder={'{\n  "channel": 9\n}'} />
                          </Form.Item>
                        )}

                        {showMetadata && (
                          <Form.Item label="Metadata JSON" name={[field.name, 'metadataText']}>
                            <Input.TextArea rows={2} placeholder={'{\n  "notes": "optional"\n}'} />
                          </Form.Item>
                        )}
                      </div>
                    )}
                  </Card>
                );
              }}
            </Form.Item>
          ))
        )}
      </Space>
    )}
    </Form.List>
  );
};

export default ModuleFormList;
